import { useCalendarContext } from "@/features/calendar/CalendarProvider";
import { type OpenModalProps } from "@/features/calendar/openCalendarModal";
import { type CoWorkerCalendarItem } from "@/features/calendar/types";
import {
  getBlockHeight,
  getCalStartHour,
  getColor,
  getIsDashed,
  getRenderTypeInfoFromCalendarItem,
} from "@/features/calendar/utils";
import { useSettings } from "@repo/ui";

import { DraggableAppointmentBlockContainer } from "../components/DraggableAppointmentBlockContainer";
import { WeekCalendarAppointment } from "./WeekCalendarAppointment";
import { AppointmentBlock } from "./components";
import { WEEK_BLOCK_HEIGHT } from "./constants";
import {
  type LaidOutItem,
  assignLanes,
  getLeftPos,
  getTopPos,
  isCancelledCalendarItem,
} from "./utils";

const MAX_OVERLAP_OFFSET = 16;
const MIN_OVERLAP_WIDTH = 30;
const BLOCK_RIGHT_MARGIN = 12;

export const AppointmentBlocks = ({
  items,
  startDate,
  allDayHeight,
  blockWidth,
  openModal,
  showInitials,
}: {
  items: CoWorkerCalendarItem[];
  startDate: Date;
  blockWidth: number;
  allDayHeight: number;
  openModal: (props: OpenModalProps) => void;
  showInitials?: boolean;
}) => {
  const { task_configuration, calendar } = useSettings();
  const { isItemSelected } = useCalendarContext();
  const blocks = [];

  const { calStartHour } = getCalStartHour(items);

  const laidOutItems: LaidOutItem[] = items.map((item) => {
    const displayStart = item.displayStart ?? item.start;
    const hourOffset = Math.max(displayStart.getHours() - calStartHour, 0);
    const minuteOffset = (displayStart.getMinutes() * WEEK_BLOCK_HEIGHT) / 60;

    return {
      item,
      top: getTopPos(allDayHeight, hourOffset, minuteOffset),
      height: getBlockHeight(item, WEEK_BLOCK_HEIGHT),
      baseLeft: getLeftPos(startDate, displayStart, blockWidth),
      spanMinutes: item.length,
    };
  });

  const { laneAssignments, totalLanesForItem } = assignLanes(laidOutItems);

  for (let index = 0; index < laidOutItems.length; index++) {
    const { item, top, height, baseLeft } = laidOutItems[index];
    const blockInstanceId = `${item.id}-${item.start.getTime()}-${item.end.getTime()}`;
    const appointmentOverlapIndex = laneAssignments[index];
    const appointmentOverlapCount = totalLanesForItem[index];

    const availableWidth = Math.max(blockWidth - BLOCK_RIGHT_MARGIN, 0);
    const hasOverlap = appointmentOverlapCount > 1;
    const perLaneWidth = hasOverlap
      ? availableWidth / appointmentOverlapCount
      : availableWidth;
    const width = hasOverlap
      ? Math.max(perLaneWidth, MIN_OVERLAP_WIDTH)
      : availableWidth;

    const overlapShift = hasOverlap
      ? perLaneWidth >= MIN_OVERLAP_WIDTH
        ? appointmentOverlapIndex * perLaneWidth
        : appointmentOverlapIndex *
          Math.max(
            0,
            Math.min(
              MAX_OVERLAP_OFFSET,
              (availableWidth - MIN_OVERLAP_WIDTH) /
                (appointmentOverlapCount - 1)
            )
          )
      : 0;

    const isCancelled = isCancelledCalendarItem(item);
    const zIndex = hasOverlap
      ? isCancelled
        ? appointmentOverlapIndex + 1
        : 100 + appointmentOverlapIndex
      : 1;
    const left = baseLeft + overlapShift;
    const renderinfo = getRenderTypeInfoFromCalendarItem(item);
    const colorInfo = getColor(
      renderinfo,
      task_configuration?.task_types,
      calendar?.activity_configuration
    );

    blocks.push(
      <DraggableAppointmentBlockContainer
        key={blockInstanceId}
        id={blockInstanceId}
        item={item}
        top={top}
        left={left}
        width={width}
        height={height}
        zIndex={zIndex}
        verticalMode
        onClick={() => {
          if (item.appointmentType === "task") {
            openModal({ type: "task", id: item.id });
          } else if (item.appointmentType === "appointment") {
            openModal({
              type: "appointment",
              opportunityId: item.opportunityId,
              appointmentId: Number(item.id),
              id: item.id,
            });
          } else if (item.appointmentType === "outlook") {
            openModal({ type: "outlook", item });
          } else if (item.appointmentType === "scheduleActivity") {
            openModal({ type: "scheduled_activity", item });
          }
        }}
      >
        <AppointmentBlock
          key={blockInstanceId}
          backgroundColor={colorInfo.color}
          borderColor={colorInfo.borderColor}
          dashed={getIsDashed(renderinfo)}
          selected={isItemSelected(item.id)}
        >
          <WeekCalendarAppointment
            item={item}
            overlapping={appointmentOverlapCount > 1}
            showInitials={showInitials}
          />
        </AppointmentBlock>
      </DraggableAppointmentBlockContainer>
    );
  }
  return <>{blocks}</>;
};
